// RESILIENCE SCREENER — Scoring Engine
// Scale: 0-4 per item · Max score = 100 (25 items × 4)
// Risk banding: Low / Moderate / High resilience
import { RESILIENCE_ITEMS, RESILIENCE_DIMS } from "./questionBank.js";

export const RS_NORMS = {
  overall: { mean:72.4, sd:14.2, n:980 },
  by_sector: {
    healthcare:  { mean:74.1, sd:13.8 },
    call_centre: { mean:68.6, sd:15.1 },
    armed_forces:{ mean:81.2, sd:11.4 },
    sales:       { mean:70.3, sd:14.6 },
    general:     { mean:72.4, sd:14.2 },
  }
};

// Risk bands — based on total score out of 100
export const RS_BANDS = [
  { min:0,  max:40, label:"Low Resilience",      level:"low",
    color:"#dc2626", bg:"#fef2f2", border:"#fecaca",
    icon:"🔴",
    hire:"Higher risk for sustained high-pressure roles. Robust support structure required.",
    desc:"Candidate may find sustained high-pressure working conditions significantly challenging. Not recommended for roles with high emotional or physical demand without strong support systems." },
  { min:40, max:65, label:"Moderate Resilience", level:"moderate",
    color:"#d97706", bg:"#fffbeb", border:"#fde68a",
    icon:"🟡",
    hire:"Suitable for most roles with supportive management. Monitor during onboarding.",
    desc:"Adequate resilience for most professional settings. Some risk under sustained extreme pressure. Suitable with good manager support and structured onboarding." },
  { min:65, max:85, label:"High Resilience",     level:"high",
    color:"#059669", bg:"#f0fdf4", border:"#86efac",
    icon:"🟢",
    hire:"Recommended for high-pressure roles. Well-equipped to handle demanding environments.",
    desc:"Demonstrates strong resilience profile. Likely to perform consistently under pressure, recover from setbacks and maintain effectiveness in demanding environments." },
  { min:85, max:101,label:"Exceptional Resilience",level:"exceptional",
    color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe",
    icon:"🔵",
    hire:"Strong fit for high-stakes, high-pressure or adversity-heavy roles.",
    desc:"Top-quartile resilience. Suited for front-line emergency, high-stakes leadership or extreme-environment roles. Strong emotional regulation and purposeful under adversity." },
];

// Dim-level risk thresholds (out of max 20, 5 items × 4)
export const RS_DIM_THRESHOLDS = {
  H: { low:8,  moderate:13, high:17 },
  R: { low:8,  moderate:13, high:17 },
  A: { low:8,  moderate:13, high:17 },
  E: { low:8,  moderate:13, high:17 },
  C: { low:7,  moderate:12, high:16 }, // Emotional control stricter for healthcare/armed forces
};

// Role-critical dimensions by market
export const RS_ROLE_CRITICAL = {
  healthcare:   { critical:["C","R"], label:"Healthcare" },
  call_centre:  { critical:["C","E"], label:"Call Centre" },
  armed_forces: { critical:["H","A","C"], label:"Armed Forces" },
  sales:        { critical:["R","E"], label:"Field Sales" },
  general:      { critical:["H","C"], label:"General" },
};

export function scoreResilience(answers) {
  const dimScores = { H:0,R:0,A:0,E:0,C:0 };
  const dimCounts = { H:0,R:0,A:0,E:0,C:0 };

  RESILIENCE_ITEMS.forEach((item,i)=>{
    const raw = answers[i];
    if (raw===null||raw===undefined) return;
    dimScores[item.dim] += Number(raw);
    dimCounts[item.dim]++;
  });

  const maxPerDim = 20; // 5 items × 4
  const dimResults = {};
  let total=0, maxTotal=0;

  Object.keys(RESILIENCE_DIMS).forEach(dim=>{
    const raw   = dimScores[dim];
    const count = dimCounts[dim];
    const max   = count*4;
    const pct   = max>0 ? Math.round((raw/max)*100) : 0;
    const score100 = Math.round((raw/maxPerDim)*100); // normalised to 0-100
    const thresh = RS_DIM_THRESHOLDS[dim];
    const level = raw>=thresh.high?"high":raw>=thresh.moderate?"moderate":"low";
    dimResults[dim] = { raw, max, pct, score100, level, count };
    total    += raw;
    maxTotal += (count*4);
  });

  const totalPct = maxTotal>0 ? Math.round((total/maxTotal)*100) : 0;
  const totalScore100 = Math.round((total/100)*100); // out of 100
  const band = RS_BANDS.find(b=>totalPct>=b.min&&totalPct<b.max)||RS_BANDS[0];

  const norm = RS_NORMS.overall;
  const z = (total - norm.mean) / norm.sd;
  const percentile = Math.min(99, Math.max(1, Math.round(50+z*17)));

  return {
    dimResults,
    total, maxTotal, totalPct,
    percentile, band,
    scales:{ resilience: percentile },
    perfAvg: totalPct/20,
    potAvg:  totalPct/20,
    perfBox: totalPct>=65?3:totalPct>=40?2:1,
    potBox:  totalPct>=65?3:totalPct>=40?2:1,
  };
}
